export function concatIterables<T>(...iterables: Iterable<T>[]): Iterable<T> {
  return createIterable(function* createIterator() {
    for (const iterable of iterables) {
      yield* iterable;
    }
  });
}

export function isEmptyIterable(iterable: Iterable<any>): boolean {
  return !someInIterable(iterable, () => true);
}

export function someInIterable<T>(iterable: Iterable<T>, predicate: (value: T) => boolean): boolean {
  for (const value of iterable) {
    if (predicate(value)) {
      return true;
    }
  }

  return false;
}

export function everyInIterable<T>(iterable: Iterable<T>, predicate: (value: T) => boolean): boolean {
  for (const value of iterable) {
    if (!predicate(value)) {
      return false;
    }
  }

  return true;
}

export function firstInIterable<T, U>(iterable: Iterable<T>, defaultValue: U): T | U;
export function firstInIterable<T>(iterable: Iterable<T>): T | undefined;
export function firstInIterable<T>(iterable: Iterable<T>, defaultValue = undefined) {
  return findInIterable(iterable, () => true, defaultValue);
}

export function findInIterable<T, U, V extends T>(
  iterable: Iterable<T>,
  predicate: (value: T) => value is V,
  defaultValue: U,
): V | U;
export function findInIterable<T, U>(
  iterable: Iterable<T>,
  predicate: (value: T) => boolean,
  defaultValue: U,
): T | U;
export function findInIterable<T, V extends T>(
  iterable: Iterable<T>,
  predicate: (value: T) => value is V,
): V | undefined;
export function findInIterable<T>(iterable: Iterable<T>, predicate: (value: T) => boolean): T | undefined;
export function findInIterable<T, U>(
  iterable: Iterable<T>,
  predicate: (value: T) => boolean,
  defaultValue?: U,
): T | U | undefined {
  for (const value of iterable) {
    if (predicate(value)) {
      return value;
    }
  }

  return defaultValue;
}

export function mapIterable<T, V>(iterable: Iterable<T>, project: (value: T) => V): Iterable<V> {
  return createIterable(function* createIterator() {
    for (const value of iterable) {
      yield project(value);
    }
  });
}

export function flatMapIterable<T, V>(
  iterable: Iterable<T>,
  project: (value: T) => Iterable<V>,
): Iterable<V> {
  return createIterable(function* createIterator() {
    for (const value of iterable) {
      yield* project(value);
    }
  });
}

export function filterIterable<T, V extends T>(
  iterable: Iterable<T>,
  predicate: (value: T) => value is V,
): Iterable<V>;
export function filterIterable<T>(iterable: Iterable<T>, predicate: (value: T) => boolean): Iterable<T>;
export function filterIterable<T>(iterable: Iterable<T>, predicate: (value: T) => boolean): Iterable<T> {
  return createIterable(function* createIterator() {
    for (const value of iterable) {
      if (predicate(value)) {
        yield value;
      }
    }
  });
}

export function takeFromIterable<T>(iterable: Iterable<T>, count: number): Iterable<T> {
  return createIterable(function* createIterator() {
    let yieldedCount = 0;
    for (const value of iterable) {
      if (yieldedCount < count) {
        yieldedCount += 1;
        yield value;
      } else {
        break;
      }
    }
  });
}

export function reduceIterable<T, U>(
  iterable: Iterable<T>,
  callback: (previousValue: U, currentValue: T, currentIndex: number, iterable: Iterable<T>) => U,
  initialValue: U,
) {
  let result = initialValue;
  let i = 0;

  for (const value of iterable) {
    result = callback(result, value, i, iterable);
    i += 1;
  }

  return result;
}

export function createIterable<T>(createIterator: () => Iterator<T>): Iterable<T> {
  return {
    [Symbol.iterator]: createIterator,
  };
}

export function indexOf<T>(iterable: Iterable<T>, item: T): number {
  let i = 0;
  for (const yielded of iterable) {
    if (yielded === item) {
      return i;
    }
    i += 1;
  }
  return -1;
}
